import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SpaceInfo } from 'src/api/types';
import { BuildingSelectItem } from 'src/components/BuildingSelect/BuildingSelect';
import { FloorSelectItem } from 'src/components/FloorSelect/FloorSelect';
import { TownSelectItem } from 'src/components/TownSelect/TownSelect';
import { ProgressState } from 'src/types';

import { startAppListening } from '../listenerMiddleware';
import { StoreState } from '../types';

import {
  emptySpaces,
  spacesFirstPageNumber,
  initialState,
  emptyBookingObjects,
} from './initialState';
import { getSpacesState } from './selectors';
import {
  spacesPageSize,
  fetchSpaces,
  fetchSpaceMapData,
  removeSpace,
} from './services';
import { MapInfo } from './types';

export const spacesSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    setTown: (state, action: PayloadAction<TownSelectItem | undefined>) => {
      state.town = action.payload;
      state.building = undefined;
      state.floor = undefined;
    },
    setBuilding: (
      state,
      action: PayloadAction<BuildingSelectItem | undefined>,
    ) => {
      state.building = action.payload;
      state.floor = undefined;
    },
    setFloor: (state, action: PayloadAction<FloorSelectItem | undefined>) => {
      state.floor = action.payload;
    },
    setMap: (state, action: PayloadAction<MapInfo>) => {
      state.town = {
        id: action.payload.townId,
        value: {
          townId: action.payload.townId,
          townName: action.payload.townName,
        },
      };
      state.building = {
        id: action.payload.buildingId,
        value: {
          townId: action.payload.townId,
          buildingId: action.payload.buildingId,
          address: action.payload.buildingAddress,
        },
      };
      state.floor = {
        id: action.payload.floorId,
        value: {
          townId: action.payload.townId,
          buildingId: action.payload.buildingId,
          floorId: action.payload.floorId,
          title: action.payload.title,
          inheritedEnabled: true,
          planUnderlayLoaded: true,
        },
      };
    },
    clearMap: state => {
      state.town = undefined;
      state.building = undefined;
      state.floor = undefined;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    setRemovingState: (state, action: PayloadAction<number>) => {
      state.removings.add(action.payload);
    },
    setSavingsState: (
      state,
      action: PayloadAction<{ id: string; value: ProgressState }>,
    ) => {
      state.savings[action.payload.id] = action.payload.value;
    },
    openSpace: (state, action: PayloadAction<SpaceInfo | null>) => {
      state.space = action.payload;
      state.spaceDialogIsOpen = true;
      state.town = undefined;
      state.building = undefined;
      state.floor = undefined;
      state.backgroundData = undefined;
      state.bookingObjects = emptyBookingObjects;
    },
    closeSpace: state => {
      state.space = null;
      state.spaceDialogIsOpen = false;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchSpaces.pending, (state, action) => {
      if (action.meta.arg) {
        state.rows = emptySpaces;
        state.hasMore = true;
        state.pageNumber = spacesFirstPageNumber;
      }
      state.error = undefined;
      state.loading = true;
      state.currentRequestId = action.meta.requestId;
    });
    builder.addCase(fetchSpaces.rejected, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.error = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchSpaces.fulfilled, (state, action) => {
      if (!action.payload || state.currentRequestId !== action.meta.requestId)
        return;

      const items = action.payload;

      state.loading = false;
      state.hasMore = items.length >= spacesPageSize;
      state.pageNumber++;
      state.rows = action.meta.arg ? items : [...state.rows, ...items];
    });

    builder.addCase(fetchSpaceMapData.pending, state => {
      state.mapError = undefined;
      state.mapLoading = true;
    });
    builder.addCase(fetchSpaceMapData.rejected, (state, action) => {
      state.mapError = action.payload;
      state.mapLoading = false;
    });
    builder.addCase(fetchSpaceMapData.fulfilled, (state, action) => {
      state.mapLoading = false;
      state.bookingObjects = action.payload.bookingObjects;
      state.backgroundData = action.payload.backgroundData;
      state.relatedSpaces = action.payload.relatedSpaces;
    });

    builder.addCase(removeSpace.pending, (state, action) => {
      state.removings.add(action.meta.arg);
    });
    builder.addCase(removeSpace.rejected, (state, action) => {
      state.removings.delete(action.meta.arg);
    });
    builder.addCase(removeSpace.fulfilled, (state, action) => {
      state.removings.delete(action.meta.arg);
    });
  },
});

export const { actions: spacesActions, reducer: spacesReducer } = spacesSlice;

startAppListening({
  predicate: (action, current, previous) => {
    return current.spaces.search !== previous.spaces.search;
  },
  effect: (action, listenerApi) => {
    listenerApi.dispatch(fetchSpaces(true));
  },
});

startAppListening({
  predicate: (action, current, previous) => {
    return current.spaces.floor?.id !== previous.spaces.floor?.id;
  },
  effect: async (action, listenerApi) => {
    const { floor } = getSpacesState(listenerApi.getState() as StoreState);
    if (floor?.value.floorId)
      listenerApi.dispatch(fetchSpaceMapData(floor.value.floorId));
  },
});
